import type { BehaviorGraph } from "../layout/types.js";
import type { BehaviorNode, Port, VariableValueType } from "./types.js";
import {
  getPathEntryAtPath,
  getPathEntryValueType,
  getPathSchemaAtPath,
  getFlattenedPathOptions,
  type PathEntry,
} from "../layout/data-source-schema.js";

type ItemType = Exclude<VariableValueType, "list">;
type Context = {
  valueType: string;
  source?: string;
  base?: string;
  itemType?: ItemType;
};
type Resolved = { node: BehaviorNode; outputs: Map<string, Context> };
const itemOperators = new Set([
  "get",
  "find",
  "findByPath",
  "findByCondition",
  "currentItem",
  "comparator",
]);
const listOperators = new Set([
  "filter",
  "sort",
  "sortBy",
  "take",
  "push",
  "remove",
]);
const objectOperators = new Set([
  "extendObject",
  "setProperty",
  "pickObject",
  "omitObject",
]);
const joinPath = (base?: string, field?: string) =>
  [base, field].filter(Boolean).join(".") || undefined;

/** Keep valid saved nested selections visible instead of showing an unrelated first field. */
export function getBehaviorObjectFieldOptions(
  schema: PathEntry[] | null,
  base?: string,
  selected?: string,
) {
  const fields = getPathSchemaAtPath(schema, base);
  const options = getFlattenedPathOptions(fields, { includeChildren: false });
  if (
    selected &&
    !options.some((option) => option.value === selected) &&
    getPathEntryAtPath(fields, selected)
  ) {
    options.push({ value: selected, label: selected.split(".").join(" → ") });
  }
  return options;
}

/** Resolve typed object paths from the wires, independently of saved node order or stale source metadata. */
export function synchronizeBehaviorObjectPaths(
  graph: BehaviorGraph,
  schema: Record<string, PathEntry[] | null>,
): BehaviorGraph {
  const byId = new Map(graph.nodes.map((node) => [node.id, node]));
  const incoming = new Map(
    graph.connections.map((edge) => [
      `${edge.toNodeId}:${edge.toPortId}`,
      edge,
    ]),
  );
  const resolved = new Map<string, Resolved>();
  const visiting = new Set<string>();
  const hasSchema = (source?: string) =>
    !!source && Array.isArray(schema[source]);

  function resolve(id: string): Resolved | undefined {
    const cached = resolved.get(id);
    if (cached) return cached;
    const original = byId.get(id);
    if (!original || visiting.has(id)) return undefined;
    visiting.add(id);
    let node = original;
    const input = (portId: string): Context | undefined => {
      const edge = incoming.get(`${id}:${portId}`);
      return edge && resolve(edge.fromNodeId)?.outputs.get(edge.fromPortId);
    };
    const source = "objectSource" in node ? node.objectSource : undefined;
    const sourceContext = (valueType: string): Context => ({
      valueType,
      ...(hasSchema(source)
        ? {
            source,
            base:
              node.type === "operator" ? node.objectFieldPathBase : undefined,
          }
        : {}),
      itemType:
        "listItemValueType" in node ? node.listItemValueType : undefined,
    });
    let context = sourceContext(
      node.ports.find((port) => port.direction === "output")?.valueType ??
        "string",
    );

    const update = (
      metadata: Record<string, unknown>,
      types: Record<string, string> = {},
    ) => {
      const ports = node.ports.map((port): Port => {
        const type = types[port.id];
        return !type || port.valueType === type
          ? port
          : { ...port, valueType: type, value: undefined };
      });
      if (
        Object.entries(metadata).some(
          ([key, value]) =>
            (node as unknown as Record<string, unknown>)[key] !== value,
        ) ||
        ports.some((port, index) => port !== node.ports[index])
      ) {
        node = { ...node, ...metadata, ports } as BehaviorNode;
      }
    };

    if (
      node.type === "variable" &&
      node.valueType === "list" &&
      hasSchema(source)
    ) {
      context = { valueType: "list", source, itemType: "object" };
      update({ listItemValueType: "object" });
    } else if (node.type === "operator") {
      const upstream = input("input1");
      if (node.operator === "get" && node.mode === "unary") {
        const object = incoming.has(`${id}:input1`)
          ? upstream
          : sourceContext("object");
        if (object?.valueType === "object" && hasSchema(object.source)) {
          const paths = schema[object.source!]!;
          const field =
            node.objectFieldPath &&
            getPathEntryAtPath(
              paths,
              joinPath(object.base, node.objectFieldPath),
            )
              ? node.objectFieldPath
              : getPathSchemaAtPath(paths, object.base)?.[0]?.path;
          const valueType =
            getPathEntryValueType(paths, field, object.base) ?? "string";
          update(
            {
              objectSource: object.source,
              objectFieldPathBase: object.base,
              objectFieldPath: field,
            },
            { output: valueType },
          );
          context = {
            valueType,
            source: object.source,
            base: joinPath(object.base, field),
            itemType: valueType === "list" ? "object" : undefined,
          };
        }
      } else if (upstream?.valueType === "list" && hasSchema(upstream.source)) {
        const itemType = upstream.itemType ?? "object";
        if (itemOperators.has(node.operator)) {
          const metadata: Record<string, unknown> = {
            objectSource: upstream.source,
            objectFieldPathBase: upstream.base,
            listItemValueType: itemType,
          };
          const types: Record<string, string> = { output: itemType };
          if (node.operator === "comparator") {
            types.outputA = itemType;
            types.outputB = itemType;
          }
          if (node.operator === "find") {
            const paths = schema[upstream.source!]!;
            const field =
              node.objectFieldPath &&
              getPathEntryAtPath(
                paths,
                joinPath(upstream.base, node.objectFieldPath),
              )
                ? node.objectFieldPath
                : getPathSchemaAtPath(paths, upstream.base)?.[0]?.path;
            metadata.objectFieldPath = field;
            types.input2 =
              getPathEntryValueType(paths, field, upstream.base) ?? "string";
          }
          update(metadata, types);
          context = { ...upstream, valueType: itemType };
        } else if (listOperators.has(node.operator)) {
          update(
            {
              objectSource: upstream.source,
              objectFieldPathBase: upstream.base,
              listItemValueType: itemType,
            },
            node.operator === "push" ? { input2: itemType } : {},
          );
          context = upstream;
        }
      } else if (
        objectOperators.has(node.operator) &&
        upstream?.valueType === "object" &&
        hasSchema(upstream.source)
      ) {
        context = upstream;
      }
      if (node.operator === "mapItems") {
        const mapped = input("input2");
        if (mapped && hasSchema(mapped.source)) {
          const itemType = (
            mapped.valueType === "list" ? "object" : mapped.valueType
          ) as ItemType;
          update({
            objectSource: mapped.source,
            objectFieldPathBase: mapped.base,
            listItemValueType: itemType,
          });
          context = { ...mapped, valueType: "list", itemType };
        }
      }
    }

    const outputs = new Map(
      node.ports
        .filter((port) => port.direction === "output")
        .map((port): [string, Context] => {
          if (port.id === "outputIndex")
            return [port.id, { valueType: "number" }];
          if (port.objectTypeId && hasSchema("@object:" + port.objectTypeId))
            return [
              port.id,
              {
                valueType: port.valueType,
                source: "@object:" + port.objectTypeId,
                itemType: context.itemType,
              },
            ];
          return [port.id, { ...context, valueType: port.valueType }];
        }),
    );
    const result = { node, outputs };
    resolved.set(id, result);
    visiting.delete(id);
    return result;
  }

  const nodes = graph.nodes.map((node) => resolve(node.id)?.node ?? node);
  return nodes.some((node, index) => node !== graph.nodes[index])
    ? { ...graph, nodes }
    : graph;
}
